import React, { useState, useEffect } from "react";
import { Table, Button, Form, Input, Spin, message, Tabs, Modal, Popconfirm } from "antd";
import { PlusOutlined } from '@ant-design/icons';
import Breadcrumbs from "../../../components/Breadcrumbs";
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAllIPAddresses,
  createIPAddress,
  updateIPAddress,
  deleteIPAddress,
  clearErrors,
  resetSuccess
} from "../../../store/slices/IPAddressSlice";

const Settings = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingIp, setEditingIp] = useState(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("1");
  
  const dispatch = useDispatch();
  const ipAddresses = useSelector((state) => state.ipAddresses.ipAddresses);
  const ipLoading = useSelector((state) => state.ipAddresses.loading);
  const success = useSelector((state) => state.ipAddresses.success);
  const error = useSelector((state) => state.ipAddresses.error);
  
  // Fetch IP addresses when component mounts
  useEffect(() => {
    dispatch(fetchAllIPAddresses());
  }, [dispatch]);

  // Handle success and error messages
  useEffect(() => {
    if (success) {
      setIsModalVisible(false);
      setEditingIp(null);
      form.resetFields();
      setTimeout(() => {
        dispatch(resetSuccess());
      }, 2000);
    }
    
    if (error) {
      message.error(error);
      dispatch(clearErrors());
    }
  }, [success, error, dispatch, form]);
  
  // Add new IP address
  const handleAddIp = (values) => {
    // Validate input data before dispatching
    if (!values.address || !values.addressName) {
      message.error('Both IP address and name are required');
      return;
    }
    
    const ipData = {
      addressName: values.addressName.trim(),
      address: values.address.trim()
    };
    
    dispatch(createIPAddress(ipData));
  };

  // Update IP address
  const handleUpdateIp = (values) => {
    // Validate input data before dispatching
    if (!values.address || !values.addressName) {
      message.error('Both IP address and name are required');
      return;
    }
    
    const ipData = {
      addressName: values.addressName.trim(),
      address: values.address.trim()
    };
    
    dispatch(updateIPAddress({ id: editingIp._id, ipData }));
  };

  // Delete IP address
  const handleDeleteIp = (id) => {
    dispatch(deleteIPAddress(id));
  };

  const showAddModal = () => {
    setEditingIp(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingIp(record);
    form.setFieldsValue({
      address: record.address,
      addressName: record.addressName || "",
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingIp(null);
    form.resetFields();
  };

  const handleSubmit = (values) => {
    if (editingIp) {
      handleUpdateIp(values);
    } else {
      handleAddIp(values);
    }
  };
  
  const handleTabChange = (key) => {
    setActiveTab(key);
  };
  
  const columns = [
    {
      title: "Name",
      dataIndex: "addressName",
      key: "addressName",
    },
    {
      title: "IP Address",
      dataIndex: "address",
      key: "address",
    },
    {
      title: "Action",
      className: "text-end",
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <div className="dropdown dropdown-action text-end">
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            <Link
              className="dropdown-item"
              to="#"
              onClick={() => showEditModal(record)}
            >
              <i className="fa fa-pencil m-r-5" /> Edit
            </Link>
            <Popconfirm
              title="Are you sure you want to delete this IP address?"
              onConfirm={() => handleDeleteIp(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Link
                className="dropdown-item"
                to="#"
              >
                <i className="fa fa-trash m-r-5" /> Delete
              </Link>
            </Popconfirm>
          </div>
        </div>
      ),
    },
  ];

  const items = [
    {
      key: '1',
      label: 'IP Address',
      children: (
        <div className="tab-content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="card-title mb-0">IP Address Management</h4>
                    <p className="text-muted small mt-2">
                      <i className="fa fa-info-circle mr-1"></i>
                      &nbsp; To allow access from all IPs, add <code>0.0.0.0</code> to the allowed IPs list.
                    </p>
                  </div>
                  <Button
                    type="primary" 
                    style={{ backgroundColor: '#2C3E50', borderColor: '#2C3E50' }}
                    icon={<PlusOutlined />}
                    onClick={showAddModal}
                  >
                    Add IP Address
                  </Button>
                </div>
                <div className="card-body">
                  {ipLoading ? (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      <Spin size="large" />
                    </div>
                  ) : (
                    <Table 
                      columns={columns} 
                      dataSource={ipAddresses}
                      rowKey="_id"
                      pagination={{ pageSize: 10 }}
                      locale={{
                        emptyText: 'No IP addresses are present'
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle="Settings"
          title="Main"
          subtitle="Settings"
          modalClass="col-auto float-end ms-auto"
        />
        
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <Tabs 
                  defaultActiveKey="1" 
                  activeKey={activeTab}
                  onChange={handleTabChange}
                  type="card"
                  className="settings-tabs"
                  items={items}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Ant Design Modal for IP Address Add/Edit */}
        <Modal
          title={editingIp ? "Edit IP Address" : "Add IP Address"}
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          destroyOnClose={true}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="address"
              label="IP Address"
              tooltip="Use 0.0.0.0 to allow access from all IP addresses"
              rules={[
                { required: true, message: "Please enter IP address" },
                {
                  pattern: /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                  message: "Please enter a valid IP address",
                },
              ]}
            >
              <Input placeholder="e.g., 192.168.1.1 or 0.0.0.0 for all IPs" />
            </Form.Item>
            
            <Form.Item
              name="addressName"
              label="Name"
              rules={[{ required: true, message: "Please enter name" }]}
            >
              <Input placeholder="e.g., Office Server" />
            </Form.Item>
            
            <Form.Item className="text-end">
              <Button type="primary" htmlType="submit" loading={ipLoading} style={{ marginRight: 8 }}>
                {editingIp ? "Update" : "Add"}
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default Settings;
